import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useState, type ReactNode } from "react";

/**
 * Single QueryClient instance for the app. Defaults chosen to keep refetch
 * traffic sane while data still feels fresh:
 *
 *   staleTime: 60s  — components mounting within a minute reuse cached data,
 *                     no spam on tab switches or route changes.
 *   gcTime:    5min — unused queries hang around long enough to back-cache hits
 *                     when the user revisits a page.
 *   retry:     1    — single retry on transient failure (default of 3 is too
 *                     eager; HttpError is typed and rarely flaky).
 *   refetchOnWindowFocus: false — tab switching shouldn't trigger a fetch
 *                                  storm. Per-query opt-in is cheap if needed.
 *
 * Devtools render only in development (mode !== "production"), per Vite's
 * import.meta.env.DEV gate.
 *
 * See frontend/FRONTEND_AGENT_RULES.md §2 — server state lives in React Query,
 * not Zustand.
 */
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

export function QueryProvider({ children }: { children: ReactNode }) {
  // useState ensures a single QueryClient instance per app lifecycle, even
  // through HMR. Hot module reloads of this module recreate the function but
  // reuse the existing client instance.
  const [client] = useState(makeQueryClient);

  return (
    <QueryClientProvider client={client}>
      {children}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />}
    </QueryClientProvider>
  );
}
